import { Fragment, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Paper, Masthead } from "@/components/Paper";
import { Logo } from "@/components/Logo";
import { STEP_LABELS } from "@/lib/api";
import { BRIEFING, BRIEFING_STEPS, BRIEFING_LORE, CHARGE } from "@/lib/briefing";

const isBeat = (spk) => spk === "DIRECTION" || spk === "SFX";
export const HIDE_NOTE = "Hide this in the building — do not place with the rest of the pack.";
export const PROD_ONLY = "PRODUCTION ONLY — DO NOT PLACE IN ROOM";
export function A4Page({ children, pageRef, compact = false }) {
  const outer = useRef(null);
  useLayoutEffect(() => {
    const box = outer.current;
    const over = box.firstChild.scrollHeight > box.clientHeight + 1;
    box.toggleAttribute("data-overflow", over);
  });
  const setRef = (el) => {
    outer.current = el;
    if (pageRef) pageRef(el);
  };
  return <section className={`a4-page ${compact ? "a4-compact" : ""}`} ref={setRef}><div className="a4-inner">{children}</div></section>;
}

// Splits n items across A4 pages by whole items so every page prints at 100% without overflow.
function useFlow(n) {
  const [cuts, setCuts] = useState([n]);
  const boxes = useRef([]);
  useLayoutEffect(() => {
    for (let p = 0; p < cuts.length; p += 1) {
      const box = boxes.current[p];
      const start = p ? cuts[p - 1] : 0;
      if (box && box.firstChild.scrollHeight > box.clientHeight + 1 && cuts[p] - start > 1) {
        const limit = box.getBoundingClientRect().bottom - 1;
        const spill = Array.from(box.querySelectorAll("[data-bi]")).find((el) => el.getBoundingClientRect().bottom > limit);
        const at = spill ? Number(spill.dataset.bi) : cuts[p] - 1;
        const next = [...cuts];
        next[p] = Math.max(start + 1, Math.min(cuts[p] - 1, at));
        if (p === cuts.length - 1) next.push(n);
        setCuts(next);
        return;
      }
    }
  }, [cuts, n]);
  useLayoutEffect(() => {
    if (document.fonts && document.fonts.status !== "loaded") document.fonts.ready.then(() => setCuts([n]));
  }, [n]);
  const ranges = cuts.map((end, p) => [p ? cuts[p - 1] : 0, end]);
  const refFor = (p) => (el) => { boxes.current[p] = el; };
  return { ranges, refFor };
}

export function FlowDoc({ doc, banner, children, aside, compact }) {
  const { ranges, refFor } = useFlow(doc.blocks.length);
  return ranges.map((range, p) => (
    <A4Page key={p} pageRef={refFor(p)} compact={compact}>
      {banner && <div className="prod-banner">{banner}</div>}
      <Paper doc={doc} testid="print-paper" part={{ range, n: p + 1, total: ranges.length }} aside={p === ranges.length - 1 ? aside : null}>
        {p === ranges.length - 1 ? children : null}
      </Paper>
      {banner && <div className="prod-banner">{banner}</div>}
    </A4Page>
  ));
}

const PoliceHead = ({ caption }) => (
  <div className="flex items-end justify-between pb-6 border-b-4 border-[#0033A1]">
    <Logo height={40} />
    <span className="text-[15px] font-bold text-[#505a5f]">{caption}</span>
  </div>
);

function StationSign({ s, openingLine }) {
  return (
    <A4Page>
      <div className="a4-fill police-page flex flex-col">
        <PoliceHead caption="Major Investigation Team · Operation SPORE" />
        <div className="bg-[#0033A1] text-white px-8 py-5 mt-8 text-[30px] font-black">Suspect interview station</div>
        <div className="flex-1 pt-10">
          <div className="suspect-photo !w-40 !h-40 !text-6xl">{s.initials}</div>
          <h1 className="text-[76px] font-black leading-[1.02] mt-10">{s.name}</h1>
          <p className="text-[32px] text-[#505a5f] mt-5">{s.role}</p>
          <p className="text-[22px] mt-8"><strong>Statement reference:</strong> {s.tape_ref}</p>
          <p className="text-[24px] mt-12 inset">Open <strong>{s.name}</strong> from Suspect interviews on the case computer.</p>
          {s.mode !== "ai" && (
            <div className="mt-8 border-4 border-[#0b0c0c] p-6">
              <p className="text-[24px] font-black">Dr Kit Marsh is here in person. Question him in the room.</p>
              <p className="text-[24px] mt-3 italic">"{openingLine}"</p>
            </div>
          )}
        </div>
        <div className="pt-5 border-t border-[#b1b4b6] text-[15px] text-[#505a5f]">Operation SPORE · Major Investigation Team</div>
      </div>
    </A4Page>
  );
}

const DocPage = ({ doc, prodOnly, compact }) => <FlowDoc doc={doc} banner={prodOnly ? PROD_ONLY : null} compact={compact} />;

const RecoveryCode = ({ code }) => (
  <div className="recovery-box" data-testid="print-recovery-code">
    <div className="recovery-label">RECOVERY CODE</div>
    <div className="recovery-code">{code}</div>
  </div>
);

// Cult leaflet keeps the code under the verse; lab/military papers carry it beside the signature line.
const CluePage = ({ doc, code }) => (doc.family === "cult"
  ? <FlowDoc doc={doc}><RecoveryCode code={code} /></FlowDoc>
  : <FlowDoc doc={doc} compact aside={<RecoveryCode code={code} />} />);

function TranscriptPage({ s, lines, n, total, pageRef, first }) {
  return (
    <A4Page pageRef={pageRef}>
      <article className="paper mil print-doc a4-fill">
        {n === 1 ? (
          <>
            <Masthead doc={{ family: "military", ref: s.tape_ref }} />
            <h3 className="font-bold text-[34px] leading-tight mt-7">Interview transcript</h3>
            <div className="text-[#5f6f78] text-lg mt-1">Military Police interview with {s.name} · Recorded interview · {s.tape_ref}</div>
          </>
        ) : (
          <div className="flex justify-between font-mono text-[12px] tracking-wider text-[#6b7a82] pb-3 border-b border-[#d6d9d8]">
            <span>INTERVIEW TRANSCRIPT · {s.tape_ref}</span><span>{s.name.toUpperCase()}</span>
          </div>
        )}
        <div className="mt-5 space-y-2.5 text-[16px] leading-[1.5] flex-1">
          {lines.map(([spk, txt], i) => (isBeat(spk)
            ? <p key={i} data-bi={first + i} className="t-beat">[{txt}]</p>
            : <p key={i} data-bi={first + i}><span className={`t-speaker ${spk === "INTERVIEWER" ? "t-interviewer" : "t-suspect"}`}>{spk}</span>{txt}</p>))}
        </div>
        <div className="pt-6 flex justify-between font-mono text-[12px] tracking-wider text-[#6b7a82]">
          <span>RECOVERY INDEX {s.tape_ref}</span><span>PAGE {n} OF {total}</span>
        </div>
      </article>
    </A4Page>
  );
}

function TranscriptFlow({ s, lines }) {
  const { ranges, refFor } = useFlow(lines.length);
  return ranges.map(([a, b], i) => <TranscriptPage key={i} pageRef={refFor(i)} s={s} first={a} lines={lines.slice(a, b)} n={i + 1} total={ranges.length} />);
}

const transcriptPages = (s, lines) => [<TranscriptFlow key="t" s={s} lines={lines} />];

const briefingPages = () => [
  <A4Page key="b1">
    <div className="a4-fill police-page">
      <PoliceHead caption={BRIEFING.caption} />
      <h1 className="text-[44px] font-black mt-8">{BRIEFING.title}</h1>
      <p className="inset text-[20px] mt-5"><strong>{BRIEFING.anyOrder}</strong> {BRIEFING.anyOrderRest}</p>
      <ol className="border-l-2 border-[#b1b4b6] ml-5 space-y-4 mt-7">
        {BRIEFING_STEPS.map(([t, d], i) => (
          <li key={t} className="relative pl-10">
            <span className="step-num absolute -left-[23px] top-0">{i + 1}</span>
            <h2 className="text-[24px] font-black pt-1.5">{t}</h2>
            <p className="text-[18px] mt-1 leading-normal">{d}</p>
          </li>
        ))}
      </ol>
    </div>
  </A4Page>,
  <A4Page key="b2">
    <div className="a4-fill police-page">
      <PoliceHead caption={BRIEFING.caption} />
      <h1 className="text-[40px] font-black mt-8">{BRIEFING.background}</h1>
      <div className="mt-5 space-y-4">
        {BRIEFING_LORE.map(([t, d]) => (
          <section key={t}>
            <h3 className="text-[24px] font-black text-[#0033A1]">{t}</h3>
            <p className="text-[18px] leading-snug mt-1">{d}</p>
          </section>
        ))}
      </div>
    </div>
  </A4Page>,
];

const Lines = ({ n }) => <div className="mt-3">{Array.from({ length: n }, (_, i) => <div key={i} className="write-line" />)}</div>;

const notesPage = (c, sids, n) => (
  <A4Page key={`n${n}`}>
    <div className="a4-fill police-page">
      <PoliceHead caption="Paper backup" />
      <h1 className="text-[40px] font-black mt-8">Case notes{n > 1 ? " (continued)" : ""}</h1>
      <p className="text-[18px] text-[#505a5f] mt-1">Record what you find for each suspect. Secrets you uncover will be recorded here.</p>
      {sids.map((sid) => {
        const s = c.suspects[sid];
        return (
          <div key={sid} className="mt-7 border-t-4 border-[#0033A1] pt-3">
            <div className="flex items-baseline justify-between"><h2 className="text-[24px] font-black">{s.name}</h2><span className="text-[16px] text-[#505a5f]">{s.role}</span></div>
            <div className="flex flex-wrap gap-x-6 gap-y-2 mt-2 text-[16px]">
              {STEP_LABELS.map(([k, label]) => <span key={k} className="flex items-center gap-2"><span className="check-box" />{label}</span>)}
            </div>
            <div className="flex items-center gap-3 mt-3 text-[16px]"><strong>Recovery code</strong>{[0, 1, 2, 3].map((i) => <span key={i} className="code-cell" />)}</div>
            <Lines n={6} />
          </div>
        );
      })}
    </div>
  </A4Page>
);

const caseNotesPages = (c) => [
  notesPage(c, c.order.slice(0, 2), 1),
  notesPage(c, c.order.slice(2), 2),
  <A4Page key="n2">
    <div className="a4-fill police-page">
      <PoliceHead caption="Paper backup" />
      <h1 className="text-[40px] font-black mt-8">{CHARGE.title}</h1>
      <p className="text-[19px] mt-3">{CHARGE.intro}</p>
      <h2 className="text-[28px] font-black mt-8">{CHARGE.question}</h2>
      <span className="form-hint">{CHARGE.select}</span>
      <div className="grid grid-cols-2 gap-x-8 gap-y-5 mt-5">
        {c.order.map((sid) => (
          <div key={sid} className="flex items-start gap-4">
            <span className="radio-circle" />
            <span><span className="block text-[22px] font-bold">{c.suspects[sid].name}</span><span className="block text-[16px] text-[#505a5f]">{c.suspects[sid].role}</span></span>
          </div>
        ))}
      </div>
      <h2 className="text-[28px] font-black mt-10">{CHARGE.grounds}</h2>
      <span className="form-hint">{CHARGE.groundsHint}</span>
      <Lines n={9} />
      <div className="grid grid-cols-2 gap-8 mt-8 text-[17px]"><div>Signed: <div className="write-line" /></div><div>Date: <div className="write-line" /></div></div>
    </div>
  </A4Page>,
];

const docsPages = (docs, prodOnly, compact) => docs.map((d, i) => <DocPage key={i} doc={d} prodOnly={prodOnly} compact={compact} />);

export function buildPacks(c) {
  const suspectPack = (sid) => {
    const s = c.suspects[sid];
    return {
      id: `suspect-${sid}`, kind: "suspect", title: `Suspect pack · ${s.name}`, file: `Suspect_pack_${s.short}`,
      assets: [
        { id: `${sid}-sign`, title: "Station sign", pages: () => [<StationSign key="s" s={s} openingLine={c.opening_line} />] },
        { id: `${sid}-profile`, title: "Profile", pages: () => docsPages([c.profiles[sid]]) },
        { id: `${sid}-transcript`, title: "Interview transcript", pages: () => transcriptPages(s, c.transcripts[sid]) },
        { id: `${sid}-crosscheck`, title: "Cross-check document", pages: () => docsPages([c.crosschecks[sid]], false, true) },
        { id: `${sid}-clue`, title: "Hidden coded clue", note: HIDE_NOTE, pages: () => [<CluePage key="c" doc={c.clues[sid]} code={c.codes[sid]} />] },
      ],
    };
  };
  const p = c.production;
  return [
    {
      id: "info", kind: "info", title: "Info pack", file: "Info_pack",
      assets: [
        { id: "briefing", title: "Briefing", pages: briefingPages },
        { id: "casenotes", title: "Case notes & charge decision sheet", pages: () => caseNotesPages(c) },
      ],
    },
    ...c.order.map(suspectPack),
    {
      id: "production", kind: "production", title: "Production pack", file: "Production_pack",
      assets: [
        { id: "marsh-brief", title: "Marsh actor brief", pages: () => docsPages(p.marsh_brief, true) },
        { id: "answer-sheet", title: "Host answer & code sheet", pages: () => docsPages(p.answer_sheet, true) },
        { id: "reset-checklist", title: "Show reset checklist", pages: () => docsPages(p.reset_checklist, true) },
        { id: "hiding-log", title: "Clue hiding-place log", pages: () => docsPages(p.hiding_log, true) },
        { id: "video-brief", title: "CCTV video production brief", docx: VIDEO_BRIEF_DOCX, pages: () => docsPages([c.video_brief], true) },
      ],
    },
  ];
}

export const VIDEO_BRIEF_DOCX = "/api/production/video-brief.docx";export const videoBriefAsset = (packs) => packs.find((p) => p.id === "production").assets.find((a) => a.id === "video-brief");

export const renderAssets = (assets) => assets.flatMap((a) => a.pages().map((pg, i) => <Fragment key={`${a.id}-${i}`}>{pg}</Fragment>));

export const PrintRoot = ({ assets }) => createPortal(<div id="print-root" className="print-root">{renderAssets(assets)}</div>, document.body);

export function collectCss() {
  let out = "";
  Array.from(document.styleSheets).forEach((sh) => {
    try {
      Array.from(sh.cssRules).forEach((r) => {
        if (r.type !== 3) out += `${r.cssText}\n`;
      });
    } catch (e) {
      /* cross-origin font sheet: the PDF renderer loads fonts itself */
    }
  });
  return out;
}
